<template>
  <v-layout :loading="loading">
    <template v-if="!loading">
      <d-header
        fluid
        class="unit-page"
      >
        <!-- Unit header (new_design/marine): region badge, address split in
             big blocks, city / postal code with map + copy, actions right -->
        <div class="unit-head">
          <div class="unit-region-row">
            <span
              v-if="model.region"
              class="unit-region-badge"
            >
              <i class="mdi mdi-map-marker-outline" />
              {{ model.region.name }}
            </span>
            <span class="unit-rule" />
          </div>

          <div class="unit-address">
            <div
              v-if="civicNumber"
              class="unit-field"
            >
              <div class="unit-label">
                {{ $t('civic_number') }}
              </div>
              <div class="unit-value is-big">
                {{ civicNumber }}
              </div>
            </div>
            <span
              v-if="civicNumber"
              class="unit-sep"
            />
            <div class="unit-field">
              <div class="unit-label">
                {{ $t('street') }}
              </div>
              <div class="unit-value is-big">
                {{ streetName }}
              </div>
            </div>
            <template v-if="model.address && model.address.address_2">
              <span class="unit-sep" />
              <div class="unit-field">
                <div class="unit-label">
                  {{ $t('address_2') }}
                </div>
                <div class="unit-value is-big">
                  {{ model.address.address_2 }}
                </div>
              </div>
            </template>
            <div class="is-flex is-align-items-center card visibility-group unit-actions">
              <v-activate-property-status
                :property-status="model.latest_status"
                @success="query"
              />
              <button
                v-if="model.latest_status && $roleCan([$laravel.role_admin])"
                class="button is-small is-white"
                type="button"
                @click="togglePublicVisibility(model.latest_status)"
              >
                <div
                  class="icon"
                  :class="{
                    'has-text-success': model.latest_status.publicly_visible,
                    'has-text-danger': !model.latest_status.publicly_visible,
                  }"
                >
                  <i
                    class="mdi mdi-24px"
                    :class="{
                      'mdi-eye-outline': model.latest_status.publicly_visible,
                      'mdi-eye-off-outline': !model.latest_status.publicly_visible,
                    }"
                  />
                </div>
              </button>
              <!-- Public page: green link when the ad is public, greyed out
                   and disabled otherwise -->
              <a
                v-if="isShownInPublicPlatform"
                :href="'/public/'+model.slug"
                target="_blank"
                class="button is-small is-white public-link"
                :title="$t('show_on_public_link').capitalize()"
              >
                <div class="icon has-text-success">
                  <i class="mdi mdi-open-in-new mdi-24px" />
                </div>
              </a>
              <button
                v-else
                type="button"
                class="button is-small is-white public-link is-off"
                disabled
                :title="$t('off_line_property')"
              >
                <div class="icon">
                  <i class="mdi mdi-open-in-new mdi-24px" />
                </div>
              </button>
              <a
                :href="googleMapsUrl"
                target="_blank"
                rel="noopener"
                class="button is-small is-white title-tool"
                :title="$t('open_in_google_maps')"
              >
                <div class="icon"><i class="mdi mdi-map-outline mdi-24px" /></div>
              </a>
              <button
                type="button"
                class="button is-small is-white title-tool"
                :title="$t('copy_address')"
                @click="copyAddress"
              >
                <div class="icon"><i class="mdi mdi-content-copy mdi-24px" /></div>
              </button>
            </div>
          </div>

          <div class="unit-meta">
            <div class="unit-field">
              <div class="unit-label">
                {{ $t('city') }}
              </div>
              <div class="unit-value">
                {{ model.address ? model.address.city : "" }}
              </div>
            </div>
            <div class="unit-field">
              <div class="unit-label">
                {{ $t('postal_code') }}
              </div>
              <div class="unit-value">
                {{ model.address ? model.address.postal_code : "" }}
              </div>
            </div>
          </div>
        </div>
      </d-header>
      <d-container
        fluid
        class="unit-page"
      >
        <!-- Mobile: one select instead of a scrolling tab bar -->
        <b-field
          v-if="$mq === 'mobile'"
          class="unit-tab-select"
        >
          <b-select
            :value="activeTab"
            expanded
            @input="goToTab"
          >
            <option
              v-for="tab in tabOptions"
              :key="tab.name"
              :value="tab.name"
            >
              {{ tab.label }}
            </option>
          </b-select>
        </b-field>
        <div
          v-else
          class="tabs is-left"
        >
          <ul>
            <li
              :class="{
                'is-active':
                  $route.name === 'properties.show.details'
              }"
            >
              <router-link
                :to="{
                  name: 'properties.show.details',
                  params: { property: model.id }
                }"
              >
                {{ $t("details").capitalize() }}
              </router-link>
            </li>
            <li
              :class="{
                'is-active': $route.name === 'properties.show.pictures'
              }"
            >
              <router-link
                :to="{
                  name: 'properties.show.pictures',
                  params: { property: model.id }
                }"
              >
                {{ $t("pictures").capitalize() }}
              </router-link>
            </li>
            <li
              :class="{
                'is-active': $route.name === 'properties.show.statuses' ||
                  $route.name?.startsWith('properties.show.statuses.')
              }"
            >
              <router-link
                :to="{
                  name: 'properties.show.statuses',
                  params: { property: model.id }
                }"
              >
                {{ $t("property_statuses").capitalize() }}
              </router-link>
            </li>
            <li
              :class="{
                'is-active': $route.name === 'properties.show.property-inspections' ||
                  $route.name?.startsWith('properties.show.property-inspections.')
              }"
            >
              <router-link
                :to="{
                  name: 'properties.show.property-inspections',
                  params: { property: model.id }
                }"
              >
                {{ $t("property_inspections").capitalize() }}
                <!-- Open work items (to do / in progress) at a glance -->
                <span
                  v-if="model.count_inspections"
                  class="tag is-danger is-rounded tab-count ml-2"
                >
                  {{ model.count_inspections }}
                </span>
              </router-link>
            </li>

            <li
              v-if="$roleCan([$laravel.role_admin, $laravel.role_employee])"
              :class="{
                'is-active': $route.name === 'properties.show.application-files' ||
                  $route.name?.startsWith('properties.show.application-files.')
              }"
            >
              <router-link
                :to="{
                  name: 'properties.show.application-files',
                  params: { property: model.id }
                }"
              >
                {{ $t("application_files").capitalize() }}
              </router-link>
            </li>
          </ul>
        </div>

        <router-view
          :key="model.id"
          :model="model"
          @fetch="query"
        />
      </d-container>
    </template>
  </v-layout>
</template>

<script>
import notificator from "@/services/notificator";
import ActivateForRentStatusComponent from "./components/ActivateForRentStatusComponent.vue";

export default {
  components: {
    "v-activate-property-status": ActivateForRentStatusComponent,
  },
  metaInfo() {
    return {
      title: this.title,
      __property: this.title // for breadcrumb
    };
  },
  data() {
    return {
      model: null,
      billing_address: {},
      loading: true
    };
  },
  computed: {
    isShownInPublicPlatform() {
      return (
        this.model?.latest_status &&
        this.model.latest_status.type === window.Laravel.STATUS_FOR_RENT &&
        this.model.latest_status.publicly_visible &&
        this.model.latest_status.activated_at
      );
    },
    title() {
      if (this.model && this.model.full_address_with_unity) {
        return this.model.full_address_with_unity;
      }
      return "";
    },
    // "1540 Rue Décarie" -> "1540" + "Rue Décarie" (number may be 1540A or 1540-1542)
    addressParts() {
      const address = this.$shortStreet((this.model && this.model.address && this.model.address.address_1) || "");
      const match = address.trim().match(/^(\d+[A-Za-z]?(?:-\d+[A-Za-z]?)?)\s+(.+)$/);

      return match ? { number: match[1], street: match[2] } : { number: "", street: address };
    },
    civicNumber() {
      return this.addressParts.number;
    },
    streetName() {
      return this.addressParts.street;
    },
    tabOptions() {
      const tabs = [
        { name: "properties.show.details", label: this.$t("details").capitalize() },
        { name: "properties.show.pictures", label: this.$t("pictures").capitalize() },
        { name: "properties.show.statuses", label: this.$t("property_statuses").capitalize() },
        {
          name: "properties.show.property-inspections",
          label: this.$t("property_inspections").capitalize()
            + (this.model && this.model.count_inspections ? " (" + this.model.count_inspections + ")" : ""),
        },
      ];

      if (this.$roleCan([this.$laravel.role_admin, this.$laravel.role_employee])) {
        tabs.push({ name: "properties.show.application-files", label: this.$t("application_files").capitalize() });
      }

      return tabs;
    },
    // Child routes (statuses/:id, inspections/create…) map to their tab
    activeTab() {
      const name = this.$route.name || "";
      const match = this.tabOptions.find((tab) => name === tab.name || name.startsWith(tab.name + "."));

      return match ? match.name : (name === "properties.edit" ? "properties.show.details" : "properties.show.details");
    },
    // "3400 Rue Louis Pasteur, #301, Trois-Rivières, G8Z 1X1"
    fullAddressToCopy() {
      const address = (this.model && this.model.address) || {};
      const street = [address.address_1, address.address_2 ? "#" + address.address_2 : null]
        .filter(Boolean)
        .join(", ");

      return [street, address.city, address.postal_code].filter(Boolean).join(", ");
    },
    googleMapsUrl() {
      const address = (this.model && this.model.address) || {};
      const query = [address.address_1, address.city, address.postal_code].filter(Boolean).join(", ");

      return "https://www.google.com/maps/search/?api=1&query=" + encodeURIComponent(query);
    },
  },
  watch: {
    $route(to, from) {
      this.query();
    }
  },
  created() {
    this.query();
  },
  methods: {
    goToTab(name) {
      if (name && name !== this.$route.name) {
        this.$router.push({ name, params: { property: this.model.id } });
      }
    },
    copyAddress() {
      const text = this.fullAddressToCopy;
      const done = () => notificator.open({ message: this.$t("address_copied"), type: "success" });

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(done).catch(() => this.copyFallback(text, done));
      } else {
        this.copyFallback(text, done);
      }
    },
    copyFallback(text, done) {
      const input = document.createElement("textarea");

      input.value = text;
      document.body.appendChild(input);
      input.select();
      try {
        document.execCommand("copy");
        done();
      } finally {
        document.body.removeChild(input);
      }
    },
    // Same confirm + toggle flow as the property list
    togglePublicVisibility(latestStatus) {
      this.$buefy.dialog.confirm({
        title: this.$t("confirm_public_visibility_title"),
        message: this.$t(
          latestStatus.publicly_visible
            ? "confirm_public_visibility_not_visible_description"
            : "confirm_public_visibility_visible_description"
        ),
        confirmText: this.$t("confirm"),
        cancelText: this.$t("cancel"),
        type: latestStatus.publicly_visible ? "is-danger" : "is-success",
        onConfirm: () => {
          this.$http
            .patch(
              this.$laravel.route("api.property.statuses.public_visibility", {
                property: latestStatus.property_id,
                propertyStatus: latestStatus.id,
              })
            )
            .then(response => {
              this.query();
              notificator.open({
                message: response.data.message,
                type: "success",
              });
            })
            .catch(e => {
              console.error(e);
            });
        },
      });
    },
    query() {
      this.$http
        .get(
          this.$laravel.route("api.properties.show", {
            property: this.$route.params.property
          })
        )
        .then(response => {
          this.loading = false;
          this.model = response.data.data;
        })
        .catch(e => {
          console.error(e);
          this.$router.push("/properties");
        });
    }
  }
};
</script>

<style scoped>
/* Design navy for the public page icon; grey when disabled */
.public-link .icon .mdi,
.title-tool .icon .mdi {
  color: #0d3b7f;
}
.public-link.is-off,
.public-link.is-off:disabled {
  opacity: 1;
  cursor: not-allowed;
  background: transparent;
}
.public-link.is-off .icon .mdi {
  color: #c5c5c5;
}
.tab-count {
  height: 1.5em;
  min-width: 1.5em;
  padding: 0 0.5em;
  font-size: 0.75rem;
  font-weight: 700;
}
</style>
